/**
 * Global Notification WebSocket Service
 *
 * Singleton service to manage a single WebSocket connection for notifications
 * across the entire application. Prevents duplicate connections and messages.
 */

import { reactive } from 'vue';

class NotificationService {
  constructor() {
    this.ws = null;
    this.state = reactive({
      isConnected: false,
      isConnecting: false,
    });
    this.listeners = new Map(); // listener_id -> callback
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.reconnectTimeout = null;
    this.userId = null;
    this.schema = null;
  }

  /**
   * Connect to notifications WebSocket
   * @param {number} userId - User ID
   * @param {string} schema - Database schema
   */
  connect(userId, schema = null) {
    if (this.state.isConnected || this.state.isConnecting) {
      console.log('[NotificationService] Already connected or connecting');
      return;
    }

    this.userId = userId;
    this.schema = schema || localStorage.getItem('selectedSchema') || 'safetycv';
    this.fechadoManualmente = false;

    const token = this._getToken();
    if (!token) {
      console.error('[NotificationService] No token available');
      return;
    }

    const wsUrl = this._getWebSocketUrl(userId, token, this.schema);
    console.log('[NotificationService] Connecting to:', wsUrl);

    this.state.isConnecting = true;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('[NotificationService] Connected');
        this.state.isConnected = true;
        this.state.isConnecting = false;
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[NotificationService] Message received:', data);
          this._notifyListeners(data);
        } catch (e) {
          console.error('[NotificationService] Error parsing message:', e);
        }
      };

      this.ws.onerror = (event) => {
        console.error('[NotificationService] WebSocket error:', event);
      };

      this.ws.onclose = (event) => {
        console.log('[NotificationService] Disconnected');
        this.state.isConnected = false;
        this.state.isConnecting = false;

        // Auto-reconnect if not max attempts (nunca depois de um disconnect())
        if (!this.fechadoManualmente && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`[NotificationService] Reconnecting... (attempt ${this.reconnectAttempts})`);

          this.reconnectTimeout = setTimeout(() => {
            this.connect(this.userId, this.schema);
          }, this.reconnectDelay);
        }
      };
    } catch (e) {
      console.error('[NotificationService] Failed to create WebSocket:', e);
      this.state.isConnecting = false;
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    console.log('[NotificationService] Disconnecting...');
    this.fechadoManualmente = true;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.state.isConnected = false;
    this.state.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Register a listener for notifications
   * @param {string} listenerId - Unique ID for this listener
   * @param {function} callback - Callback function to receive notifications
   * @returns {function} Unsubscribe function
   */
  subscribe(listenerId, callback) {
    console.log(`[NotificationService] Listener registered: ${listenerId}`);
    this.listeners.set(listenerId, callback);

    // Return unsubscribe function
    return () => {
      console.log(`[NotificationService] Listener unregistered: ${listenerId}`);
      this.listeners.delete(listenerId);
    };
  }

  /**
   * Notify all listeners of a new notification
   * @param {object} data - Notification data
   */
  _notifyListeners(data) {
    this.listeners.forEach((callback, listenerId) => {
      try {
        callback(data);
      } catch (e) {
        console.error(`[NotificationService] Error in listener ${listenerId}:`, e);
      }
    });
  }

  /**
   * Get authentication token
   * @returns {string|null} Token
   */
  _getToken() {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      return userData?.token || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Build WebSocket URL
   */
  _getWebSocketUrl(userId, token, schema) {
    const baseUrl = process.env.VUE_APP_URL_BASE || '';
    let host, protocol;

    if (baseUrl) {
      const urlObj = new URL(baseUrl);
      host = urlObj.host;
      protocol = urlObj.protocol === 'https:' ? 'wss:' : 'ws:';
    } else {
      host = window.location.host;
      protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    }

    return `${protocol}//${host}/ws/notifications/${userId}/?token=${token}&schema=${schema}`;
  }
}

// Export singleton instance
export default new NotificationService();
